import React, { useState, useEffect } from 'react';
import { FiSearch, FiFilter, FiCalendar, FiEye } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const OrderMonitoring = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    startDate: '',
    endDate: '',
    search: ''
  });

  useEffect(() => {
    fetchOrders();
  }, [filters]);

  const fetchOrders = async () => {
    try {
      const params = {};
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.search) params.search = filters.search;

      const response = await api.get('/superadmin/orders', { params });
      if (response.data.success) {
        setOrders(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending': return 'badge-warning';
      case 'accepted': return 'badge-info';
      case 'preparing': return 'badge-primary';
      case 'ready': return 'badge-success';
      case 'completed': return 'badge-success';
      case 'cancelled': return 'badge-danger';
      default: return 'badge-secondary';
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="order-monitoring-page">
      <div className="page-header">
        <h1>Order Monitoring</h1>
        <p>View and monitor all orders across the platform</p>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="filter-row">
          <div className="search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="Search by Order ID..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </div>

          <select
            className="form-control"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <div className="date-inputs">
            <input
              type="date"
              className="form-control"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              placeholder="Start Date"
            />
            <span>to</span>
            <input
              type="date"
              className="form-control"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              placeholder="End Date"
            />
          </div>
        </div>

        <div className="stats-summary">
          <span>Total Orders: <strong>{orders.length}</strong></span>
          <span>Total Revenue: <strong>₹{orders.reduce((sum, o) => sum + (o.pricing?.total || 0), 0).toLocaleString()}</strong></span>
        </div>
      </div>

      {/* Orders Table */}
      <div className="table-section">
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Restaurant</th>
                <th>Customer</th>
                <th>Table</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order._id}>
                  <td><code>#{order._id.slice(-8)}</code></td>
                  <td>
                    <strong>{order.vendorId?.restaurantDetails?.restaurantName || 'N/A'}</strong>
                  </td>
                  <td>
                    {order.customerId?.name || 'N/A'}
                    <br />
                    <small>{order.customerId?.mobileNumber}</small>
                  </td>
                  <td>{order.tableId?.tableNumber || 'N/A'}</td>
                  <td><strong>₹{order.pricing?.total || 0}</strong></td>
                  <td>
                    <span className={`badge ${getStatusBadgeClass(order.status)}`}>
                      {order.status}
                    </span>
                  </td>
                  <td>{new Date(order.createdAt).toLocaleString()}</td>
                  <td>
                    <button 
                      className="btn btn-sm btn-info" 
                      title="View Order Details"
                      onClick={() => setSelectedOrder(order)}
                    >
                      <FiEye />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {orders.length === 0 && (
          <div className="empty-state">
            <p>No orders found</p>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="modal-overlay" >
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2>Order Details - #{selectedOrder._id.slice(-8)}</h2>
              <button className="close-btn" onClick={() => setSelectedOrder(null)}>×</button>
            </div>
            
            <div className="modal-body">
              {/* Order Information */}
              <div className="detail-section">
                <h3>Order Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Order Number:</label>
                    <span><code>{selectedOrder.orderNumber}</code></span>
                  </div>
                  <div className="detail-item">
                    <label>Order Type:</label>
                    <span>{selectedOrder.orderType}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <span className={`badge ${getStatusBadgeClass(selectedOrder.status)}`}>
                      {selectedOrder.status}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Payment Status:</label>
                    <span className={`badge ${selectedOrder.paymentStatus === 'paid' ? 'badge-success' : 'badge-warning'}`}>
                      {selectedOrder.paymentStatus}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Payment Method:</label>
                    <span>{selectedOrder.paymentMethod || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Order Date:</label>
                    <span>{new Date(selectedOrder.createdAt).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Restaurant & Customer Info */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
                <div className="detail-section">
                  <h3>Restaurant Details</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Restaurant Name:</label>
                      <span><strong>{selectedOrder.vendorId?.restaurantDetails?.restaurantName || 'N/A'}</strong></span>
                    </div>
                    {selectedOrder.tableId && (
                      <div className="detail-item">
                        <label>Table Number:</label>
                        <span><strong>Table {selectedOrder.tableId.tableNumber}</strong></span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Customer Details</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Customer Name:</label>
                      <span>{selectedOrder.customerId?.name || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Mobile Number:</label>
                      <span><code>{selectedOrder.customerId?.mobileNumber || 'N/A'}</code></span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="detail-section">
                <h3>Order Items ({selectedOrder.items?.length || 0})</h3>
                <div className="table-container">
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left' }}>Item Name</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center' }}>Quantity</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Price</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.items?.map((item, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #dee2e6' }}>
                          <td style={{ padding: '0.75rem' }}><strong>{item.name || 'Item'}</strong></td>
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>×{item.quantity}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}>₹{item.price}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right' }}><strong>₹{item.price * item.quantity}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pricing Breakdown */}
              <div className="detail-section">
                <h3>Pricing Breakdown</h3>
                <div style={{ backgroundColor: '#f8f9fa', padding: '1.5rem', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #dee2e6' }}>
                    <span>Subtotal:</span>
                    <span>₹{selectedOrder.pricing?.subtotal || 0}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #dee2e6' }}>
                    <span>GST:</span>
                    <span>₹{selectedOrder.pricing?.gstAmount || 0}</span>
                  </div>
                  {selectedOrder.pricing?.discount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #dee2e6', color: '#28a745' }}>
                      <span>Discount:</span>
                      <span>-₹{selectedOrder.pricing?.discount}</span>
                    </div>
                  )}
                  {selectedOrder.pricing?.deliveryCharge > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #dee2e6' }}>
                      <span>Delivery Charge:</span>
                      <span>₹{selectedOrder.pricing?.deliveryCharge}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem 0', marginTop: '0.5rem', fontSize: '1.25rem', fontWeight: 'bold', color: '#2596be' }}>
                    <span>Total Amount:</span>
                    <span>₹{selectedOrder.pricing?.total || 0}</span>
                  </div>
                </div>
              </div>

              {/* Special Instructions */}
              {selectedOrder.specialInstructions && (
                <div className="detail-section">
                  <h3>Special Instructions</h3>
                  <div style={{ backgroundColor: '#fff3cd', padding: '1rem', borderRadius: '8px', border: '1px solid #ffc107' }}>
                    <p style={{ margin: 0, color: '#856404' }}>{selectedOrder.specialInstructions}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setSelectedOrder(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderMonitoring;
